import { BlockProps } from '@/components/Block/Block';
import EmojiText from '@/components/EmojiText/EmojiText';
import IconButton from '@/components/Redactie/IconButton/IconButton';
import ImagePickerModal from '@/components/Redactie/Modal/ImagePickerModal';
import P from '@/components/Redactie/P/P';
import ResponsiveImage from '@/components/ResponsiveImage/ResponsiveImage';
import { useBlockLocalUserData } from '@/hooks/useBlockLocalUserData';
import { mdiCloudUpload } from '@mdi/js';
import { Checkbox, FormControlLabel } from '@mui/material';
import { motion, LayoutGroup } from 'framer-motion';
import { useEffect, useMemo, useRef, useState, TouchEvent } from 'react';
import { ChatBlockProfilePicture } from '../ChatTextBlock/ChatTextBlock';

import styles from './ClassifyImageBlock.module.sass';

interface ClassifyImageBlockData {
    alt_text: string;
    categories: string[];
    correct_category_index: number;
}

interface ClassifyImageBlockLocalData {
    hasFinished: boolean;
}

const ClassifyImageBlock = ({
    id,
    subjectId,
    updatedAt,
    data,
    uuid,
    media,
    attachments,
    isEditable,
    isEditorPreview,
    isAnimated,
    activeChatCharacter,
    onContentHeightChanged,
    onFinished,
    onEdited,
}: BlockProps) => {
    const classifyImageBlockRef = useRef(null);
    const stickerTargetRef = useRef<HTMLDivElement>(null);

    const {
        userDataLocal,
        setUserDataLocal,
    } = useBlockLocalUserData<ClassifyImageBlockLocalData>(
        subjectId,
        id,
        updatedAt,
        isEditorPreview,
        {
            hasFinished: false,
        },
    );

    const [blockData, setBlockData] = useState<ClassifyImageBlockData>({
        alt_text: data.alt_text ?? '',
        categories: data.categories ?? ['Hond', 'Kat'],
        correct_category_index: data.correct_category_index ?? 0,
    });
    const [pickedRightAnswer, setPickedRightAnswer] = useState<boolean>(
        userDataLocal.hasFinished,
    );
    const [pickedWrongAnswer, setPickedWrongAnswer] = useState<boolean>(false);
    const [showImagePickerModal, setShowImagePickerModal] = useState<boolean>(
        false,
    );
    const [imageFile, setImageFile] = useState<File | null>(attachments?.image);
    const imageFileBase64Url = useMemo(() => {
        if (!imageFile) return null;
        return URL.createObjectURL(imageFile);
    }, [imageFile, media?.image?.url]);
    const hasImage = !!imageFile || !!media?.image?.url;

    useEffect(() => {
        setPickedRightAnswer(userDataLocal.hasFinished);
        setPickedWrongAnswer(false);
    }, [isEditable]);

    useEffect(() => {
        if (!isEditable && pickedRightAnswer) {
            setUserDataLocal({
                hasFinished: true,
            });
            onFinished?.();
        }
    }, [pickedRightAnswer]);

    useEffect(() => {
        if (!isEditable) return;
        onEdited?.({
            uuid,
            data: JSON.parse(JSON.stringify(blockData)),
            attachments: imageFile
                ? {
                      image: imageFile,
                  }
                : {},
        });
    }, [blockData, imageFile]);

    const blockTouchEvents = (event: TouchEvent<HTMLDivElement>) => {
        if (event && event.changedTouches.length > 1) event.preventDefault();
    };

    return (
        <div className="relative">
            {!!activeChatCharacter && (
                <div className="absolute" aria-hidden>
                    <ChatBlockProfilePicture
                        className="absolute top-0 left-0"
                        name={activeChatCharacter.name}
                        isTyping={false}
                    />
                </div>
            )}
            <div
                ref={classifyImageBlockRef}
                className={`${styles['classifyimageblock']} ${
                    isEditable && styles['classifyimageblock--editable']
                } ${isAnimated && styles['classifyimageblock--animated']}`}
                onTouchStart={blockTouchEvents}
                onTouchMove={blockTouchEvents}
            >
                <div className={`${styles['classifyimageblock__bubble']}`}>
                    {isEditable ? (
                        <>
                            <div className="flex flex-col">
                                <div className="relative">
                                    <ResponsiveImage
                                        imageClassName={`${styles['classifyimageblock__bubble__image']}`}
                                        responsiveImages={
                                            imageFileBase64Url
                                                ? undefined
                                                : media?.image
                                                      ?.responsive_images
                                        }
                                        alt={blockData.alt_text}
                                        src={
                                            imageFileBase64Url ??
                                            media?.image?.url
                                        }
                                    />
                                    <div
                                        className={
                                            hasImage
                                                ? 'absolute left-4 bottom-4'
                                                : 'mx-2 mt-2'
                                        }
                                    >
                                        <IconButton
                                            icon={mdiCloudUpload}
                                            label="Afbeelding wijzigen"
                                            onClick={() =>
                                                setShowImagePickerModal(true)
                                            }
                                        >
                                            Afbeelding wijzigen
                                        </IconButton>
                                    </div>
                                </div>
                                <ul className="grid grid-cols-2 gap-2 p-2">
                                    {blockData.categories.map(
                                        (category, index) => (
                                            <li
                                                key={index}
                                                className="rounded-md bg-white outline outline-2 outline-slate-200"
                                            >
                                                <P
                                                    placeholder={`Categorie ${
                                                        index + 1
                                                    }`}
                                                    rows={1}
                                                    isEditable
                                                    value={category}
                                                    onChange={(value) => {
                                                        const newCategories = [
                                                            ...blockData.categories,
                                                        ];
                                                        newCategories[
                                                            index
                                                        ] = value;
                                                        setBlockData({
                                                            ...blockData,
                                                            categories: newCategories,
                                                        });
                                                    }}
                                                />
                                                <div className="pl-2">
                                                    <FormControlLabel
                                                        value="end"
                                                        onChange={(event) => {
                                                            const checked = (event.target as HTMLInputElement)
                                                                .checked;
                                                            if (!checked)
                                                                return;
                                                            setBlockData({
                                                                ...blockData,
                                                                correct_category_index: index,
                                                            });
                                                        }}
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                checked={
                                                                    blockData.correct_category_index ===
                                                                    index
                                                                }
                                                            />
                                                        }
                                                        label={
                                                            <span className="text-sm font-bold">
                                                                Correct
                                                            </span>
                                                        }
                                                        labelPlacement="end"
                                                    />
                                                </div>
                                            </li>
                                        ),
                                    )}
                                </ul>
                            </div>

                            <ImagePickerModal
                                title="Cover afbeelding"
                                description="Geef niet het antwoord van de classificatievraag in de alternatieve tekst."
                                isOpen={showImagePickerModal}
                                originalAltText={blockData.alt_text}
                                originalImage={
                                    imageFileBase64Url ?? media?.image?.url
                                }
                                onClose={() => setShowImagePickerModal(false)}
                                imageWidth={768}
                                imageHeight={512}
                                onImageSelected={(image, altText) => {
                                    if (image) setImageFile(image);
                                    if (altText)
                                        setBlockData({
                                            ...blockData,
                                            alt_text: altText,
                                        });
                                }}
                            />
                        </>
                    ) : (
                        <LayoutGroup>
                            <fieldset
                                className={styles['classifyimageblock__board']}
                            >
                                <legend className="sr-only">
                                    Classificeer de afbeelding
                                </legend>
                                {!pickedRightAnswer && (
                                    <ul
                                        className={
                                            styles[
                                                'classifyimageblock__stickers'
                                            ]
                                        }
                                        aria-label={`Kies een sticker die het beste bij het plaatje past. ${blockData.categories.length} keuzes.`}
                                    >
                                        {blockData.categories.map(
                                            (category, index) => (
                                                <motion.button
                                                    key={category}
                                                    className={`${
                                                        styles[
                                                            'classifyimageblock__stickers__sticker'
                                                        ]
                                                    } ${
                                                        pickedWrongAnswer &&
                                                        index !==
                                                            blockData.correct_category_index
                                                            ? styles[
                                                                  'classifyimageblock__stickers__sticker--wrong'
                                                              ]
                                                            : ''
                                                    }`}
                                                    layoutId={
                                                        index ===
                                                        blockData.correct_category_index
                                                            ? `${uuid}__sticker--correct`
                                                            : undefined
                                                    }
                                                    drag
                                                    dragConstraints={{
                                                        left: -200,
                                                        top: -300,
                                                        right: 400,
                                                        bottom: 300,
                                                    }}
                                                    whileHover={{ scale: 1.1 }}
                                                    whileTap={{
                                                        scale: 0.9,
                                                        cursor: 'grabbing',
                                                    }}
                                                    whileDrag={{
                                                        scale: 1,
                                                        opacity:
                                                            index !==
                                                            blockData.correct_category_index
                                                                ? 0.2
                                                                : 1,
                                                        cursor: 'grabbing',
                                                        zIndex: 100,
                                                    }}
                                                    dragSnapToOrigin={
                                                        index !==
                                                        blockData.correct_category_index
                                                    }
                                                    onKeyDown={() =>
                                                        setPickedWrongAnswer(
                                                            false,
                                                        )
                                                    }
                                                    onMouseDown={() =>
                                                        setPickedWrongAnswer(
                                                            false,
                                                        )
                                                    }
                                                    onDragStart={() =>
                                                        setPickedWrongAnswer(
                                                            false,
                                                        )
                                                    }
                                                    onDrag={(_, info) => {
                                                        if (
                                                            !stickerTargetRef.current
                                                        )
                                                            return;
                                                        const stickerTargetRect = stickerTargetRef.current.getBoundingClientRect();
                                                        // if drag position is inside sticker target
                                                        if (
                                                            info.point.x >
                                                                stickerTargetRect.left &&
                                                            info.point.x <
                                                                stickerTargetRect.right &&
                                                            info.point.y >
                                                                stickerTargetRect.top &&
                                                            info.point.y <
                                                                stickerTargetRect.bottom
                                                        ) {
                                                            setPickedRightAnswer(
                                                                index ===
                                                                    blockData.correct_category_index,
                                                            );
                                                            setPickedWrongAnswer(
                                                                index !==
                                                                    blockData.correct_category_index,
                                                            );
                                                        }
                                                    }}
                                                    aria-pressed={false}
                                                    aria-disabled={
                                                        pickedRightAnswer
                                                    }
                                                    onClick={() => {
                                                        setPickedRightAnswer(
                                                            index ===
                                                                blockData.correct_category_index,
                                                        );
                                                        setPickedWrongAnswer(
                                                            index !==
                                                                blockData.correct_category_index,
                                                        );
                                                    }}
                                                >
                                                    <div>
                                                        <span>
                                                            <EmojiText>
                                                                {category}
                                                            </EmojiText>
                                                        </span>
                                                    </div>
                                                </motion.button>
                                            ),
                                        )}
                                    </ul>
                                )}
                                <div className="relative">
                                    <ResponsiveImage
                                        imageClassName={`${styles['classifyimageblock__bubble__image']}`}
                                        responsiveImages={
                                            imageFileBase64Url
                                                ? undefined
                                                : media?.image
                                                      ?.responsive_images
                                        }
                                        alt={blockData.alt_text}
                                        src={
                                            imageFileBase64Url ??
                                            media?.image?.url
                                        }
                                        onLoaded={() => {
                                            onContentHeightChanged?.();
                                        }}
                                    />
                                    <div
                                        className={`transfrom absolute top-6 right-6 flex h-24 w-24 -rotate-2 animate-pulse items-center justify-center rounded-2xl border-4 border-dashed border-white bg-black/40 text-center font-bold text-white shadow-inner transition-all`}
                                    >
                                        plak
                                        <br />
                                        sticker
                                    </div>
                                    <div className="absolute top-6 right-6 select-none">
                                        {!pickedRightAnswer ? (
                                            <div
                                                className="h-24 w-24"
                                                ref={stickerTargetRef}
                                            />
                                        ) : (
                                            <motion.div
                                                animate={{
                                                    rotate: '-4deg',
                                                }}
                                                layoutId={`${uuid}__sticker--correct`}
                                                className={`${styles['classifyimageblock__stickers__sticker']} ${styles['classifyimageblock__stickers__sticker--sticked']}`}
                                            >
                                                <div>
                                                    {
                                                        blockData.categories[
                                                            blockData
                                                                .correct_category_index
                                                        ]
                                                    }
                                                </div>
                                            </motion.div>
                                        )}
                                    </div>
                                </div>
                            </fieldset>
                        </LayoutGroup>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClassifyImageBlock;
